import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  fetchAdvisorData,
  uploadFiles,
  deleteFile,
  deleteAllAdvisorDocuments,
  deleteWorldSetupDocuments,
  deleteAllRoomAdvisorsDocuments,
  deleteAllRoomDocuments,
} from '@/api/configuration'
import {
  MyFile,
  ChatbotType,
  DocumentRAGSettings,
} from '@/redux/types/configuration'
import { LLMChoice } from '@/redux/types/room'
import {
  attachDocumentSettingsToFiles,
  buildDocumentSettingsMap,
} from '@/utils/documentSettingsMapper'

export const uploadFilesThunk = createAsyncThunk<
  MyFile[],
  {
    files: File[]
    directory: string
    chatbotid: string
    target?: 'advisor' | 'room' | 'dummy_bot'
    roomId?: string
  },
  { rejectValue: string }
>(
  'configuration/uploadFiles',
  async ({ files, directory, chatbotid, target, roomId }, thunkAPI) => {
    try {
      const response = await uploadFiles(files, directory, chatbotid, {
        target,
        roomId,
      })
      // The backend returns HTTP 207 (a 2xx axios treats as success) when the
      // file was stored but processing/embedding failed. Surface the real reason
      // instead of a false "uploaded successfully". (#561)
      const data: any = response.data
      const processing = data?.processing
      if (response.status === 207 || processing?.success === false) {
        const firstErr =
          processing?.errors?.[0]?.error ||
          data?.warning ||
          'Document was uploaded but could not be processed.'
        return thunkAPI.rejectWithValue(firstErr)
      }
      return data
    } catch (error: any) {
      // Prefer the backend's real message; fall back to the axios message
      // (e.g. "timeout of … exceeded", "Request failed with status code 502",
      // "Network Error") so failures are diagnosable rather than "Unknown error".
      const resp = error?.response?.data
      const message =
        resp?.error ||
        resp?.detail ||
        resp?.processing?.errors?.[0]?.error ||
        (typeof resp === 'string' ? resp : undefined) ||
        error?.message ||
        'Upload failed'
      return thunkAPI.rejectWithValue(message)
    }
  }
)

interface AdvisorData {
  greetings: string
  prompt: string
  scenario: string
  restrictions: string
  files: MyFile[]
  chatbotname?: string
  chatbottype?: ChatbotType
  is_default?: boolean
  temperature: number
  max_tokens: number
  history_limit: number
  chatbot_llm: LLMChoice
  rag_enabled: boolean
  rag_top_k: number
  rag_chunk_size: number
  rag_chunk_overlap: number
  rag_similarity_threshold: number
  rag_max_context_length: number
  currency_cost: number
  effective_currency_cost?: number
  use_room_default_documents?: boolean
  use_room_default_behavioral_config?: boolean
  document_settings?: Record<number, DocumentRAGSettings>
}

export const fetchAdvisorDataThunk = createAsyncThunk<
  AdvisorData,
  string,
  { rejectValue: string }
>('configuration/fetchAdvisorData', async (chatbotId, thunkAPI) => {
  try {
    const response = await fetchAdvisorData(chatbotId)
    const documentSettingsMap = buildDocumentSettingsMap(
      response.data.document_settings
    )
    const filesWithSettings = attachDocumentSettingsToFiles(
      response.data.files || [],
      response.data.document_settings
    )

    return {
      greetings: response.data.greetings || '',
      prompt: response.data.prompt || '',
      scenario: response.data.scenario || '',
      restrictions: response.data.restrictions || '',
      files: filesWithSettings,
      chatbotname: response.data.chatbotname,
      chatbottype: response.data.chatbottype,
      is_default: response.data.is_default,
      temperature: response.data.temperature,
      max_tokens: response.data.max_tokens,
      history_limit: response.data.history_limit,
      chatbot_llm: response.data.chatbot_llm,
      rag_enabled:
        response.data.rag_enabled !== undefined
          ? response.data.rag_enabled
          : true,
      rag_top_k: response.data.rag_top_k || 5,
      rag_chunk_size: response.data.rag_chunk_size || 1000,
      rag_chunk_overlap: response.data.rag_chunk_overlap || 200,
      rag_similarity_threshold:
        response.data.rag_similarity_threshold !== undefined
          ? response.data.rag_similarity_threshold
          : 0.2,
      rag_max_context_length: response.data.rag_max_context_length || 3000,
      currency_cost: response.data.currency_cost || 0,
      effective_currency_cost:
        response.data.effective_currency_cost ??
        response.data.currency_cost ??
        0,
      document_settings: documentSettingsMap,
      use_room_default_documents: !!response.data.use_room_default_documents,
      use_room_default_behavioral_config: !!response.data.use_room_default_behavioral_config,
    }
  } catch (error) {
    return thunkAPI.rejectWithValue((error as Error).message)
  }
})

export const deleteFileThunk = createAsyncThunk<
  number,
  {
    fileId: number
    chatbotId?: string
    target?: 'advisor' | 'room' | 'dummy_bot'
    roomId?: string
  },
  { rejectValue: string }
>('configuration/deleteFile', async ({ fileId, chatbotId, target, roomId }, thunkAPI) => {
  try {
    await deleteFile(fileId, { chatbotId, target, roomId })
    return fileId
  } catch (error) {
    return thunkAPI.rejectWithValue((error as Error).message)
  }
})

export interface DeleteAllDocumentsResult {
  success: boolean
  namespaces_cleared: number
  documents_deleted: number
  documents_unlinked: number
}

/**
 * Delete ALL RAG documents at one of four scopes (issue #563):
 *  - 'advisor'      → one advisor (requires chatbotId)
 *  - 'all-advisors' → every advisor in the room (requires roomId)
 *  - 'world-setup'  → the room-level / world-setup docs (requires roomId)
 *  - 'room'         → everything in the room: advisors + AI players + world setup (requires roomId)
 */
export const deleteAllDocumentsThunk = createAsyncThunk<
  DeleteAllDocumentsResult,
  {
    scope: 'advisor' | 'all-advisors' | 'world-setup' | 'room'
    chatbotId?: string
    roomId?: string
  },
  { rejectValue: string }
>('configuration/deleteAllDocuments', async ({ scope, chatbotId, roomId }, thunkAPI) => {
  try {
    let response
    if (scope === 'advisor') {
      if (!chatbotId) throw new Error('chatbotId is required to delete advisor documents')
      response = await deleteAllAdvisorDocuments(chatbotId)
    } else if (scope === 'world-setup') {
      if (!roomId) throw new Error('roomId is required to delete world-setup documents')
      response = await deleteWorldSetupDocuments(roomId)
    } else if (scope === 'all-advisors') {
      if (!roomId) throw new Error('roomId is required to delete all-advisors documents')
      response = await deleteAllRoomAdvisorsDocuments(roomId)
    } else {
      if (!roomId) throw new Error('roomId is required to delete room documents')
      response = await deleteAllRoomDocuments(roomId)
    }
    return response.data as DeleteAllDocumentsResult
  } catch (error) {
    return thunkAPI.rejectWithValue((error as Error).message)
  }
})
